import { useState, useEffect } from 'react';
import client from '../../api/client';
import betClient from '../../api/betClient';
import AiInsightCard from './AiInsightCard';
import { getBookmakerLabel } from '../../utils/bookmakers';


const inputCls = "px-3 py-1.5 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] text-[14px] text-[#111827] dark:text-[#e2e8f0] placeholder-[#9CA3AF] focus:outline-none focus:border-[#005BB3] dark:focus:border-[#3b82f6] transition-colors duration-150";

const TABS = [
  { key: 'accuracy', label: 'Accuracy', tip: 'closingline' },
  { key: 'value', label: 'Value', tip: 'value' },
];

export default function BookmakerAnalysis({ sport }) {
  const [tab, setTab] = useState('accuracy');
  const [season, setSeason] = useState('');
  const [seasons, setSeasons] = useState([]);

  const [accuracyData, setAccuracyData] = useState(null);
  const [valueData, setValueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch seasons when sport changes
  useEffect(() => {
    setSeason('');
    client.get(`/api/patterns/seasons?sport=${sport}`)
      .then((r) => {
        const s = r.data?.seasons || [];
        setSeasons(s);
      })
      .catch(() => setSeasons([]));
  }, [sport]);

  function fetchAll() {
    setLoading(true); setError(null);
    setAccuracyData(null); setValueData(null);

    const params = new URLSearchParams({ sport });
    if (season) params.set('season', season);

    Promise.all([
      betClient.get(`/api/patterns/bookmaker/accuracy?${params.toString()}`),
      betClient.get(`/api/patterns/bookmaker/value?${params.toString()}`),
    ])
      .then(([acc, val]) => { setAccuracyData(acc.data); setValueData(val.data); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  // Don't auto-fetch — user must click Refresh

  const activeType = tab === 'accuracy' ? 'bookmaker_accuracy' : 'bookmaker_value';
  const activeData = tab === 'accuracy' ? accuracyData : valueData;

  return (
    <div className="max-w-[1100px]">
      {/* Season selector */}
      <div className="flex items-end gap-2 mb-4">
        <div>
          <label className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] block mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Season</label>
          <select value={season} onChange={(e) => setSeason(e.target.value)} className={`w-[150px] ${inputCls}`}>
            <option value="">{seasons.length === 0 ? 'No data' : 'Select...'}</option>
            {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={fetchAll} disabled={loading}
          className="px-4 py-1.5 rounded-md bg-[#005BB3] hover:bg-[#004494] disabled:opacity-40 text-white text-[14px] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {!loading && !accuracyData && !valueData && !error && (
        <div className="py-12 text-center">
          <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Select a season and click Refresh to analyze bookmaker performance</p>
        </div>
      )}

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-3">
          <p className="text-[#DC2626] text-[12px]">Failed to load: {error}</p>
          <button onClick={fetchAll} className="text-[11px] text-[#DC2626] underline mt-1">Retry</button>
        </div>
      )}

      {loading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <div key={i} className="h-10 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}
        </div>
      )}

      {(accuracyData || valueData) && !loading && (
        <>
          {/* Underline tabs */}
          <div className="flex items-end gap-0 mb-4 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-0 mr-6 pb-2 text-[14px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
                  tab === t.key
                    ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                    : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#374151] dark:hover:text-[#e2e8f0]'
                }`}
                style={{ fontFamily: 'var(--font-sans)' }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Description */}
          {tab === 'accuracy' && accuracyData && (
            <p className="text-[13px] text-[#6B7280] dark:text-[#8892a8] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>
              {accuracyData.total_games} completed games analyzed — lower MAE = more accurate closing line
            </p>
          )}
          {tab === 'value' && valueData && (
            <p className="text-[13px] text-[#6B7280] dark:text-[#8892a8] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>
              {valueData.total_games} completed games analyzed — higher avg winning odds = better value for bettors
            </p>
          )}

          {/* Table + AI side by side */}
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            <div className="w-full lg:w-3/5 min-w-0">
              {activeData && (
                <RankedTable
                  rows={activeData.bookmakers}
                  columns={tab === 'accuracy' ? [
                    { key: 'rank', label: '#', render: (_, i) => i + 1 },
                    { key: 'bookmaker_title', label: 'Bookmaker', render: (r) => getBookmakerLabel(r.bookmaker_key) },
                    { key: 'games', label: 'Games' },
                    { key: 'mean_absolute_error', label: 'MAE', render: (r) => r.mean_absolute_error != null ? r.mean_absolute_error.toFixed(2) : '—', highlight: true },
                  ] : [
                    { key: 'rank', label: '#', render: (_, i) => i + 1 },
                    { key: 'bookmaker_title', label: 'Bookmaker', render: (r) => getBookmakerLabel(r.bookmaker_key) },
                    { key: 'games', label: 'Games' },
                    { key: 'avg_winning_odds', label: 'Avg Winner Odds', render: (r) => {
                      const v = r.avg_winning_odds;
                      return v != null ? (v > 0 ? `+${v.toFixed(0)}` : v.toFixed(0)) : '—';
                    }, highlight: true },
                  ]}
                />
              )}
            </div>
            <div className="w-full lg:w-2/5 min-w-0">
              <AiInsightCard
                key={`${activeType}-${sport}`}
                analysisType={activeType}
                params={{ sport, season: season || undefined }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function RankedTable({ rows, columns }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-[#6B7280] dark:text-[#8892a8] text-[14px]" style={{ fontFamily: 'var(--font-sans)' }}>No bookmaker data available.</p>
        <p className="text-[#9CA3AF] dark:text-[#505a6e] text-[13px] mt-1" style={{ fontFamily: 'var(--font-sans)' }}>Completed games with scores are required.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="text-[12px] uppercase tracking-[0.5px] text-[#6B7280] border-b border-[#E5E7EB] dark:border-[#1e2536]">
            {columns.map((col) => (
              <th key={col.key} className="py-2 px-3 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.bookmaker_key || i} className={`border-b border-[#E5E7EB] dark:border-[#1e2536] text-[14px] ${i === 0 ? 'bg-[rgba(0,91,179,0.04)] dark:bg-[rgba(59,130,246,0.06)]' : ''}`}>
              {columns.map((col) => (
                <td key={col.key} className={`py-2 px-3 ${col.highlight ? 'font-semibold text-[#111827] dark:text-[#e2e8f0]' : 'text-[#1F2937] dark:text-[#8892a8]'}`}
                  style={{ fontFamily: col.highlight ? 'var(--font-mono)' : 'var(--font-sans)' }}>
                  {col.render ? col.render(row, i) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
